import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    ShieldCheck, Gift, Star, Loader2, ArrowLeft, Check,
    DollarSign, Clock, Wallet, History, KeyRound, Mail, Crown, Globe, Search, XCircle,
    TrendingUp, Calendar, Zap,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { stringifyDetail } from '../lib/utils';
import { parseUtc } from '../lib/datetime';

const API_BASE = window.location.origin;

// Session security — module-level pure functions
// Inactivity-based timeout: session expires after 2 minutes of no user interaction
const SESSION_INACTIVITY_MS = 60 * 1000;        // 60s of inactivity auto-logs out
const SESSION_CHECK_INTERVAL_MS = 5 * 1000;     // Check every 5 seconds

// Security: session is kept in memory only (no sessionStorage / localStorage).
// Every fresh page load (incl. every NFC scan) triggers a full PIN re-auth.
// Additional defenses below: inactivity timer, visibilitychange-hidden auto-clear,
// and pagehide/beforeunload listeners.

function _sessionKey(cardId) { return `yulontap_staff_${cardId}`; }

// Deprecated no-op shims — kept only so any external references don't crash.
// Session is no longer persisted across page loads.
function saveStaffSession() { /* noop */ }
function touchStaffSession() { /* noop */ }
function restoreStaffSession() { return null; }
function clearStaffSession(cardId) {
    try {
        // Clean up any legacy persisted sessions from previous versions
        sessionStorage.removeItem(_sessionKey(cardId));
        localStorage.removeItem(_sessionKey(cardId));
    } catch { /* silent */ }
}

// (Legacy session-persistence helpers removed in v2.10.0 —
// session is in-memory only, every fresh scan requires PIN re-entry.)

// Centralized reason list with stripe color mapping
// Stripe color is auto-assigned — never manually selected
// A Tap’N Win prize voucher carries the prize in its wording and no money.
// `issued_by_name` is stamped at mint time and is the only marker the existing
// verify response already returns, so nothing in the voucher API changed.
const PRIZE_ISSUER = 'Tap’N Win';
const isPrizeVoucher = (v) => v?.issued_by_name === PRIZE_ISSUER && !!v?.message;


const REASON_CODES = [
    { code: 'late_food', labelKey: 'staffMenu.lateFoodReason', stripe: 'red', category: 'recovery' },
    { code: 'wrong_order', labelKey: 'staffMenu.wrongOrderReason', stripe: 'red', category: 'recovery' },
    { code: 'long_wait', labelKey: 'staffMenu.longWaitReason', stripe: 'red', category: 'recovery' },
    { code: 'manager_goodwill', labelKey: 'staffMenu.managerGoodwillReason', stripe: 'red', category: 'recovery' },
    { code: 'birthday', labelKey: 'staffMenu.birthdayReason', stripe: 'green', category: 'recognition' },
    { code: 'vip', labelKey: 'staffMenu.vipReason', stripe: 'gold', category: 'vip' },
    { code: 'other', labelKey: 'staffMenu.otherReason', stripe: 'green', category: 'recognition' },
];

const STRIPE_STYLES = {
    red: { bg: 'bg-red-500/15 border-red-500/40 hover:bg-red-500/25', text: 'text-red-400', dot: 'bg-red-500' },
    green: { bg: 'bg-emerald-500/15 border-emerald-500/40 hover:bg-emerald-500/25', text: 'text-emerald-400', dot: 'bg-emerald-500' },
    gold: { bg: 'gold-gradient-bg-subtle gold-border gold-gradient-bg-hover', text: 'gold-gradient-text', dot: 'gold-gradient-dot' },
    blue: { bg: 'bg-blue-500/15 border-blue-500/40 hover:bg-blue-500/25', text: 'text-blue-400', dot: 'bg-blue-500' },
};

export default function StaffMenu() {
    const { cardId } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();

    // phase: loading | language | setup | pin | forgot | menu | bank | rewards | issue | success | vip_display
    const [phase, setPhase] = useState('loading');
    const [showLangPicker, setShowLangPicker] = useState(false);
    const [, forceUpdate] = useState(0); // Force re-render on language change
    const [pin, setPin] = useState('');
    const [pinConfirm, setPinConfirm] = useState('');
    const [setupEmail, setSetupEmail] = useState('');
    const [setupStep, setSetupStep] = useState('email'); // email | create | confirm
    const [pinError, setPinError] = useState('');
    const [verifying, setVerifying] = useState(false);
    const [staffData, setStaffData] = useState(null);
    const [forgotEmail, setForgotEmail] = useState('');
    const [forgotMsg, setForgotMsg] = useState('');

    // Session helpers use module-level functions with cardId param

    // Org presets
    const [orgSettings, setOrgSettings] = useState(null);

    // Bank
    const [bankData, setBankData] = useState(null);
    const [bankLoading, setBankLoading] = useState(false);

    // Issue form
    const [selectedValue, setSelectedValue] = useState(null);
    const [customValue, setCustomValue] = useState('');
    const [showCustomValue, setShowCustomValue] = useState(false);
    const [selectedExpiry, setSelectedExpiry] = useState(null);
    const [customExpiry, setCustomExpiry] = useState('');
    const [showCustomExpiry, setShowCustomExpiry] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [message, setMessage] = useState('');
    const [issuing, setIssuing] = useState(false);
    const [issuedVoucher, setIssuedVoucher] = useState(null);
    const [issueError, setIssueError] = useState('');

    // VIP session
    const [vipSession, setVipSession] = useState(null);
    const [vipCountdown, setVipCountdown] = useState(0);
    const [vipActivating, setVipActivating] = useState(false);
    const [vipDelivered, setVipDelivered] = useState(null);
    const vipTimerRef = useRef(null);

    // Verify/Redeem voucher
    const [verifyCode, setVerifyCode] = useState('');
    const [verifyResult, setVerifyResult] = useState(null);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const [redeemLoading, setRedeemLoading] = useState(false);
    const [redeemSuccess, setRedeemSuccess] = useState(null);

    // Performance tracker
    const [perfData, setPerfData] = useState(null);
    const [perfLoading, setPerfLoading] = useState(false);
    const [perfTab, setPerfTab] = useState('today');

    const presetAmounts = orgSettings?.preset_amounts || [20, 25, 50, 75];
    const presetExpiry = orgSettings?.preset_expiry_days || [30, 60, 90];

    // Check PIN status on mount — restore session if available
    useEffect(() => {
        const existing = restoreStaffSession(cardId);
        if (existing && existing.staffData) {
            setPin(existing.pin);
            setStaffData(existing.staffData);
            if (existing.staffData.preferred_language) {
                i18n.changeLanguage(existing.staffData.preferred_language);
            }
            setPhase('menu');
            return;
        }
        fetch(`${API_BASE}/api/vouchers/check-pin/${cardId}`)
            .then(r => r.ok ? r.json() : { has_pins: true })
            .then(data => {
                if (data.pin_required === false) {
                    // PIN not required for this card — go straight to menu
                    setStaffData({
                        staff_name: 'Staff',
                        staff_id: 'no-pin',
                        role: 'staff',
                        card_id: cardId,
                        preferred_language: 'en',
                    });
                    setPhase('menu');
                } else if (data.needs_setup) {
                    setPhase('language');
                } else {
                    if (data.preferred_language) {
                        i18n.changeLanguage(data.preferred_language);
                    }
                    setPhase('pin');
                }
            })
            .catch(() => setPhase('pin'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId]);

    // ── Security: Mandatory PIN re-authentication on every scan ──
    // Session is in-memory only. Defenses: inactivity timer, visibilitychange→hidden
    // auto-clear, pagehide cleanup of any stale persisted sessions.
    useEffect(() => {
        if (!staffData) return; // Only active when authenticated

        const lastActivityRef = { current: Date.now() };
        const handleActivity = () => { lastActivityRef.current = Date.now(); };
        window.addEventListener('click', handleActivity, { passive: true });
        window.addEventListener('touchstart', handleActivity, { passive: true });
        window.addEventListener('keydown', handleActivity, { passive: true });
        window.addEventListener('scroll', handleActivity, { passive: true });

        // Periodic inactivity check — purely in-memory, no storage read
        const inactivityCheck = setInterval(() => {
            if (Date.now() - lastActivityRef.current > SESSION_INACTIVITY_MS) {
                setPhase('pin');
                setPin('');
                setStaffData(null);
                // Schedule toast after React flushes phase='pin' so Toaster is mounted
                setTimeout(() => {
                    toast(t('staffMenu.sessionLocked', 'Session locked for your security'), {
                        id: 'staff-session-locked',
                        duration: 2000,
                    });
                }, 50);
            }
        }, SESSION_CHECK_INTERVAL_MS);

        // Any time the tab/app is hidden (screen lock, switched apps, browser backgrounded),
        // IMMEDIATELY force re-auth. Defense-in-depth against handing the device to someone.
        const handleVisibility = () => {
            if (document.visibilityState !== 'visible') {
                setPhase('pin');
                setPin('');
                setStaffData(null);
                clearStaffSession(cardId);
                // Schedule toast after React flushes phase='pin' so Toaster is mounted
                setTimeout(() => {
                    toast(t('staffMenu.sessionLocked', 'Session locked for your security'), {
                        id: 'staff-session-locked',
                        duration: 2000,
                    });
                }, 50);
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);

        // pagehide is more reliable than beforeunload on iOS Safari
        const handlePageHide = () => clearStaffSession(cardId);
        window.addEventListener('pagehide', handlePageHide);
        window.addEventListener('beforeunload', handlePageHide);

        return () => {
            window.removeEventListener('click', handleActivity);
            window.removeEventListener('touchstart', handleActivity);
            window.removeEventListener('keydown', handleActivity);
            window.removeEventListener('scroll', handleActivity);
            clearInterval(inactivityCheck);
            document.removeEventListener('visibilitychange', handleVisibility);
            window.removeEventListener('pagehide', handlePageHide);
            window.removeEventListener('beforeunload', handlePageHide);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId, staffData]);

    // Clear session when leaving StaffMenu (SPA navigation away from /staff/:cardId)
    useEffect(() => {
        return () => {
            clearStaffSession(cardId);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardId]);

    // Check for active VIP session when entering menu
    const checkVip = useCallback(() => {
        fetch(`${API_BASE}/api/vouchers/vip/check/${cardId}`)
            .then(r => r.ok ? r.json() : { active: false })
            .then(data => {
                if (data.active) {
                    setVipSession(data);
                    setVipCountdown(data.remaining_seconds);
                } else {
                    setVipSession(null);
                    setVipCountdown(0);
                }
            })
            .catch(() => {});
    }, [cardId]);

    useEffect(() => {
        if (phase === 'menu') checkVip();
    }, [phase, checkVip]);

    // VIP countdown timer
    useEffect(() => {
        if (vipCountdown > 0 && vipSession) {
            vipTimerRef.current = setInterval(() => {
                setVipCountdown(prev => {
                    if (prev <= 1) {
                        clearInterval(vipTimerRef.current);
                        setVipSession(null);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(vipTimerRef.current);
        }
    }, [vipSession]);

    const formatCountdown = (secs) => {
        const m = Math.floor(secs / 60);
        const s = secs % 60;
        return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const activateVip = async (voucherCode) => {
        setVipActivating(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/vip/create`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, pin, voucher_code: voucherCode }),
            });
            if (res.ok) {
                const data = await res.json();
                setVipSession(data);
                setVipCountdown(data.ttl_seconds);
                setPhase('menu');
            }
        } catch { /* silent */ }
        finally { setVipActivating(false); }
    };

    const deliverVip = async () => {
        if (!vipSession) return;
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/vip/deliver/${vipSession.session_id}`, {
                method: 'POST',
            });
            if (res.ok) {
                const data = await res.json();
                setVipDelivered(data);
                setVipSession(null);
                setVipCountdown(0);
                if (vipTimerRef.current) clearInterval(vipTimerRef.current);
                setPhase('vip_display');
            }
        } catch { /* silent */ }
    };

    // ── Load staff performance data ──
    const loadPerformance = async () => {
        if (!staffData?.employee_id) return;
        setPerfLoading(true);
        try {
            // Use staff-specific endpoint that doesn't require JWT
            const res = await fetch(`${API_BASE}/api/staff/${staffData.employee_id}/performance`);
            if (res.ok) {
                const data = await res.json();
                setPerfData(data);
            }
        } catch { /* silent */ }
        setPerfLoading(false);
    };


    // Verify voucher code
    const verifyVoucher = async () => {
        if (!verifyCode.trim()) return;
        setVerifyLoading(true);
        setVerifyResult(null);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/verify-code`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: verifyCode.trim(), card_id: cardId, pin }),
            });
            if (res.ok) {
                setVerifyResult(await res.json());
            } else if (res.status === 425) {
                // P_SURVEY_GIFT_1.0 — survey gifts cannot be used the same day.
                const err = await res.json().catch(() => ({}));
                setVerifyResult({ valid: false, reason: 'NOT_YET_REDEEMABLE',
                                  redeemable_from: err.redeemable_from || null });
            } else if (res.status === 403) {
                setVerifyResult({ valid: false, reason: 'Invalid PIN' });
            } else {
                setVerifyResult({ valid: false, reason: 'Verification failed' });
            }
        } catch {
            setVerifyResult({ valid: false, reason: 'Connection error' });
        } finally {
            setVerifyLoading(false);
        }
    };

    // Execute redeem
    const executeRedeem = async () => {
        if (!verifyResult?.code) return;
        setRedeemLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/redeem`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code: verifyResult.code, pin, card_id: cardId }),
            });
            if (res.ok) {
                const data = await res.json();
                setRedeemSuccess(data);
                setPhase('redeem_success');
            } else {
                const err = await res.json().catch(() => ({}));
                setVerifyResult({ valid: false, reason: stringifyDetail(err.detail) || 'Redemption failed',
                                  redeemable_from: verifyResult.redeemable_from || null,
                                  code: verifyResult.code, value: verifyResult.value });
            }
        } catch {
            setVerifyResult({ valid: false, reason: 'Connection error' });
        } finally {
            setRedeemLoading(false);
        }
    };

    // Fetch org presets when authenticated
    useEffect(() => {
        if (phase === 'menu' || phase === 'rewards' || phase === 'issue' || phase === 'bank') {
            fetch(`${API_BASE}/api/voucher-settings/public/${cardId}`)
                .then(r => r.ok ? r.json() : null)
                .then(data => {
                    if (data) {
                        setOrgSettings(data);
                        if (!selectedExpiry) setSelectedExpiry(data.default_expiry_days || 30);
                        if (!message && data.default_message) setMessage(data.default_message);
                    }
                })
                .catch(() => {});
        }
    }, [phase, cardId]);

    // === HANDLERS ===

    const handleSetupPin = async () => {
        if (setupStep === 'email') {
            if (!setupEmail.trim()) return;
            setSetupStep('create');
            return;
        }
        if (setupStep === 'create') {
            if (pin.length !== 4) return;
            setSetupStep('confirm');
            return;
        }
        if (setupStep === 'confirm') {
            if (pinConfirm !== pin) {
                setPinError(t('staffMenu.pinsDoNotMatch'));
                return;
            }
            setVerifying(true);
            setPinError('');
            try {
                const res = await fetch(`${API_BASE}/api/vouchers/create-pin`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ card_id: cardId, email: setupEmail.trim(), pin, preferred_language: i18n.language }),
                });
                const data = await res.json();
                if (!res.ok) throw new Error(stringifyDetail(data.detail) || 'Failed');
                setStaffData({ staff_name: data.name });
                saveStaffSession(cardId, pin, { staff_name: data.name });
                setPhase('menu');
            } catch (e) {
                setPinError(e.message);
                setSetupStep('email');
            } finally {
                setVerifying(false);
            }
        }
    };

    const verifyPin = useCallback(async () => {
        if (pin.length !== 4) return;
        setVerifying(true);
        setPinError('');
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/staff-verify`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin, card_id: cardId, language: i18n.language }),
            });
            if (!res.ok) {
                const data = await res.json().catch(() => ({}));
                throw new Error(stringifyDetail(data.detail) || 'Invalid PIN');
            }
            const data = await res.json();
            setStaffData(data);
            saveStaffSession(cardId, pin, data);
            // Apply saved language preference
            if (data.preferred_language) {
                i18n.changeLanguage(data.preferred_language);
            }
            setPhase('menu');
        } catch (e) {
            setPinError(e.message);
        } finally {
            setVerifying(false);
        }
    }, [pin, cardId, i18n]);

    const handleForgotPin = async () => {
        if (!forgotEmail.trim()) return;
        setVerifying(true);
        setForgotMsg('');
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/forgot-pin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ card_id: cardId, email: forgotEmail.trim() }),
            });
            const data = await res.json();
            if (res.ok) {
                setForgotMsg(data.message || 'PIN has been reset. Please create a new PIN.');
                // After PIN reset, show language selection first, then setup
                setTimeout(() => { setPhase('language'); setSetupEmail(forgotEmail); setPin(''); setPinConfirm(''); setSetupStep('email'); setForgotMsg(''); }, 2500);
            } else {
                setForgotMsg(stringifyDetail(data.detail) || 'Unable to process');
            }
        } catch {
            setForgotMsg('Connection error');
        } finally {
            setVerifying(false);
        }
    };

    const loadBank = useCallback(async () => {
        setBankLoading(true);
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/my-bank`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ pin, card_id: cardId }),
            });
            if (res.ok) setBankData(await res.json());
        } catch { /* silent */ }
        finally { setBankLoading(false); }
    }, [pin, cardId]);

    useEffect(() => { if (phase === 'bank') loadBank(); }, [phase, loadBank]);

    const getEffectiveValue = () => showCustomValue ? (parseFloat(customValue) || 0) : (selectedValue || 0);
    const getEffectiveExpiry = () => showCustomExpiry ? (parseInt(customExpiry) || 30) : (selectedExpiry || 30);

    const issueVoucher = async () => {
        const value = getEffectiveValue();
        if (!value || !selectedReason) return;
        setIssuing(true);
        setIssueError('');
        try {
            const res = await fetch(`${API_BASE}/api/vouchers/issue`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    card_id: cardId, pin,
                    voucher_type: 'recovery_voucher', value,
                    reason_code: selectedReason,
                    message: message.trim() || null,
                    expiry_days: getEffectiveExpiry(),
                }),
            });
            if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.detail || 'Failed'); }
            setIssuedVoucher(await res.json());
            setPhase('success');
        } catch (e) { setIssueError(e.message); }
        finally { setIssuing(false); }
    };

    const formatDate = (ts) => {
        if (!ts) return '';
        const d = parseUtc(ts);
        const now = new Date();
        const diff = Math.floor((now - d) / 86400000);
        if (diff === 0) return 'Today';
        if (diff === 1) return 'Yesterday';
        return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    };

    const resetIssueForm = () => {
        setSelectedValue(null); setCustomValue(''); setShowCustomValue(false);
        setSelectedReason(null); setMessage(orgSettings?.default_message || '');
        setIssuedVoucher(null); setShowCustomExpiry(false); setIssueError('');
    };

    // === LOADING ===
    if (phase === 'loading') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
            </div>
        );
    }

    // === LANGUAGE SELECTION (First-time only) ===
    if (phase === 'language') {
        const handleLanguageSelect = (lang) => {
            i18n.changeLanguage(lang);
            setPhase('setup');
        };
        
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="staff-language-screen">
                <div className="w-full max-w-xs space-y-8 text-center">
                    <div className="space-y-2">
                        <Globe className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">Select your language</h1>
                        <p className="text-sm text-slate-400">Choisissez votre langue · Seleccione su idioma</p>
                    </div>
                    <div className="space-y-3">
                        <Button
                            data-testid="staff-lang-en"
                            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold"
                            onClick={() => handleLanguageSelect('en')}
                        >
                            English
                        </Button>
                        <Button
                            data-testid="staff-lang-fr"
                            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 text-base font-semibold"
                            onClick={() => handleLanguageSelect('fr')}
                        >
                            Fran&ccedil;ais
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // === FIRST-TIME PIN SETUP ===
    if (phase === 'setup') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="pin-setup-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <KeyRound className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">
                            {setupStep === 'email' ? t('staffMenu.createPin') : setupStep === 'create' ? t('staffMenu.createPin') : t('staffMenu.confirmPin')}
                        </h1>
                        <p className="text-sm text-slate-400">
                            {setupStep === 'email' ? t('staffMenu.enterEmail')
                                : setupStep === 'create' ? t('staffMenu.pinSubtitle')
                                : t('staffMenu.confirmPin')}
                        </p>
                    </div>

                    <div className="space-y-3">
                        {setupStep === 'email' && (
                            <Input
                                data-testid="setup-email-input"
                                type="email"
                                value={setupEmail}
                                onChange={(e) => { setSetupEmail(e.target.value); setPinError(''); }}
                                onKeyDown={(e) => e.key === 'Enter' && handleSetupPin()}
                                placeholder={t('staffMenu.emailPlaceholder')}
                                className="text-center bg-slate-900 border-slate-700 text-white h-12"
                                autoFocus
                            />
                        )}
                        {setupStep === 'create' && (
                            <Input
                                data-testid="setup-pin-input"
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={pin}
                                onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                                onKeyDown={(e) => e.key === 'Enter' && pin.length === 4 && handleSetupPin()}
                                placeholder="____"
                                className="text-center text-2xl tracking-[0.5em] bg-slate-900 border-slate-700 text-white h-14"
                                autoFocus
                            />
                        )}
                        {setupStep === 'confirm' && (
                            <Input
                                data-testid="setup-confirm-input"
                                type="password"
                                inputMode="numeric"
                                maxLength={4}
                                value={pinConfirm}
                                onChange={(e) => { setPinConfirm(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                                onKeyDown={(e) => e.key === 'Enter' && pinConfirm.length === 4 && handleSetupPin()}
                                placeholder="____"
                                className="text-center text-2xl tracking-[0.5em] bg-slate-900 border-slate-700 text-white h-14"
                                autoFocus
                            />
                        )}
                        {pinError && <p className="text-red-400 text-sm">{pinError}</p>}

                        <Button
                            data-testid="setup-continue-btn"
                            onClick={handleSetupPin}
                            disabled={
                                verifying ||
                                (setupStep === 'email' && !setupEmail.trim()) ||
                                (setupStep === 'create' && pin.length !== 4) ||
                                (setupStep === 'confirm' && pinConfirm.length !== 4)
                            }
                            className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                        >
                            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('staffMenu.continue')}
                        </Button>
                    </div>
                    <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-400">{t('staffMenu.cancel')}</button>
                </div>
            </div>
        );
    }

    // === PIN ENTRY ===
    if (phase === 'pin') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4 relative" data-testid="staff-pin-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <ShieldCheck className="w-10 h-10 text-emerald-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">{t('staffMenu.enterPin')}</h1>
                        <p className="text-sm text-slate-400">{t('staffMenu.pinSubtitle')}</p>
                    </div>
                    <div className="space-y-3">
                        <Input
                            data-testid="staff-pin-input"
                            type="password" inputMode="numeric" maxLength={4}
                            value={pin}
                            onChange={(e) => { setPin(e.target.value.replace(/\D/g, '').slice(0, 4)); setPinError(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && verifyPin()}
                            placeholder="____"
                            className="text-center text-2xl tracking-[0.5em] bg-slate-900 border-slate-700 text-white h-14"
                            autoFocus
                        />
                        {pinError && <p className="text-red-400 text-sm">{pinError}</p>}
                        <Button
                            data-testid="staff-pin-submit"
                            onClick={verifyPin}
                            disabled={pin.length !== 4 || verifying}
                            className="w-full bg-emerald-600 hover:bg-emerald-700 h-12"
                        >
                            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('staffMenu.continue')}
                        </Button>
                    </div>
                    <div className="space-y-2">
                        <button
                            data-testid="forgot-pin-link"
                            onClick={() => { setPhase('forgot'); setForgotEmail(''); setForgotMsg(''); }}
                            className="text-xs text-emerald-400 hover:text-emerald-300"
                        >
                            {t('staffMenu.forgotPin')}
                        </button>
                        <br />
                        <button onClick={() => navigate(-1)} className="text-xs text-slate-500 hover:text-slate-400">{t('staffMenu.cancel')}</button>
                    </div>
                </div>
                {/* Language toggle */}
                <button
                    data-testid="staff-lang-toggle"
                    onClick={() => setShowLangPicker(!showLangPicker)}
                    className="absolute bottom-6 right-6 w-10 h-10 rounded-full bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors"
                >
                    <Globe className="w-5 h-5 text-slate-400" />
                </button>
                {showLangPicker && (
                    <div className="absolute bottom-18 right-6 bg-slate-800 rounded-lg shadow-lg border border-slate-700 overflow-hidden" data-testid="staff-lang-picker">
                        <button
                            data-testid="staff-switch-en"
                            className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${i18n.language === 'en' ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-700'}`}
                            onClick={() => { i18n.changeLanguage('en'); setShowLangPicker(false); forceUpdate(n => n + 1); }}
                        >
                            English
                        </button>
                        <button
                            data-testid="staff-switch-fr"
                            className={`block w-full text-left px-4 py-2.5 text-sm transition-colors ${i18n.language === 'fr' ? 'bg-emerald-600/20 text-emerald-400' : 'text-slate-300 hover:bg-slate-700'}`}
                            onClick={() => { i18n.changeLanguage('fr'); setShowLangPicker(false); forceUpdate(n => n + 1); }}
                        >
                            Fran&ccedil;ais
                        </button>
                    </div>
                )}
                <Toaster position="top-center" duration={2000} data-testid="staff-session-toaster" />
            </div>
        );
    }

    // === FORGOT PIN ===
    if (phase === 'forgot') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="forgot-pin-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-2">
                        <Mail className="w-10 h-10 text-amber-500 mx-auto" />
                        <h1 className="text-lg font-semibold text-white">{t('staffMenu.forgotPinTitle')}</h1>
                        <p className="text-sm text-slate-400">{t('staffMenu.forgotPinDesc')}</p>
                    </div>
                    <div className="space-y-3">
                        <Input
                            data-testid="forgot-email-input"
                            type="email"
                            value={forgotEmail}
                            onChange={(e) => { setForgotEmail(e.target.value); setForgotMsg(''); }}
                            onKeyDown={(e) => e.key === 'Enter' && handleForgotPin()}
                            placeholder="you@company.com"
                            className="text-center bg-slate-900 border-slate-700 text-white h-12"
                            autoFocus
                        />
                        {forgotMsg && (
                            <p className={`text-sm ${forgotMsg.includes('reset') ? 'text-emerald-400' : 'text-red-400'}`}>
                                {forgotMsg}
                            </p>
                        )}
                        <Button
                            data-testid="forgot-submit-btn"
                            onClick={handleForgotPin}
                            disabled={!forgotEmail.trim() || verifying}
                            className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                        >
                            {verifying ? <Loader2 className="w-4 h-4 animate-spin" /> : t('staffMenu.sendReset')}
                        </Button>
                    </div>
                    <button onClick={() => { setPhase('pin'); setPin(''); }} className="text-xs text-slate-500 hover:text-slate-400">{t('staffMenu.backToLogin')}</button>
                </div>
            </div>
        );
    }

    // === STAFF MENU (MY BANK + REWARDS) ===
    if (phase === 'menu') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="staff-menu-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="space-y-1">
                        <p className="text-sm text-slate-400">{t('staffMenu.welcome')} {staffData?.staff_name}</p>
                        {staffData?.organization && (
                            <p className="text-xs text-slate-500">{staffData.organization.name}</p>
                        )}
                    </div>
                    <div className="space-y-3">
                        <Button
                            data-testid="staff-my-bank"
                            className="w-full h-16 bg-gradient-to-r from-violet-600 to-violet-700 hover:from-violet-700 hover:to-violet-800 justify-start px-5 gap-4 text-base"
                            onClick={() => setPhase('bank')}
                        >
                            <Wallet className="w-6 h-6" />
                            {t('staffMenu.myBank')}
                        </Button>
                        <Button
                            data-testid="staff-rewards"
                            className="w-full h-16 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 justify-start px-5 gap-4 text-base"
                            onClick={() => setPhase('rewards')}
                        >
                            <Gift className="w-6 h-6" />
                            {t('staffMenu.rewards')}
                        </Button>
                        <Button
                            data-testid="staff-verify-voucher"
                            className="w-full h-16 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 justify-start px-5 gap-4 text-base"
                            onClick={() => { setVerifyCode(''); setVerifyResult(null); setRedeemSuccess(null); setPhase('verify'); }}
                        >
                            <Search className="w-6 h-6" />
                            {t('staffMenu.verifyVoucher')}
                        </Button>
                        <Button
                            data-testid="staff-my-performance"
                            className="w-full h-16 justify-start px-5 gap-4 text-base text-white border-0"
                            style={{ background: 'linear-gradient(135deg, #C5960C 0%, #E8D164 40%, #D4A017 70%, #B8860B 100%)' }}
                            onClick={() => { loadPerformance(); setPhase('performance'); }}
                        >
                            <TrendingUp className="w-6 h-6" />
                            {t('staffMenu.myPerformance', 'MY PERFORMANCE')}
                        </Button>
                        {vipSession && vipCountdown > 0 && (
                            <div className="space-y-1">
                                <Button
                                    data-testid="staff-vip-btn"
                                    className="w-full h-16 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 justify-start px-5 gap-4 text-base"
                                    onClick={deliverVip}
                                >
                                    <Crown className="w-6 h-6" />
                                    {t('staffMenu.vip')}
                                </Button>
                                <p className="text-xs text-red-400/80" data-testid="vip-countdown">
                                    {t('staffMenu.expiresIn')} {formatCountdown(vipCountdown)}
                                </p>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={() => { clearStaffSession(cardId); setPhase('pin'); setPin(''); setStaffData(null); }}
                        className="text-xs text-slate-500 hover:text-slate-400"
                    >
                        {t('staffMenu.lock')}
                    </button>
                </div>
            </div>
        );
    }

    // === MY BANK ===
    if (phase === 'bank') {
        return (
            <div className="min-h-screen bg-slate-950 p-4" data-testid="my-bank-screen">
                <div className="max-w-sm mx-auto space-y-5">
                    <button onClick={() => setPhase('menu')} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> {t('staffMenu.back')}
                    </button>

                    <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                        <Wallet className="w-5 h-5 text-violet-400" /> {t('staffMenu.myBank')}
                    </h2>

                    {bankLoading ? (
                        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-400" /></div>
                    ) : bankData ? (
                        <>
                            {/* Balance */}
                            <div className="bg-gradient-to-br from-violet-900/60 to-violet-800/40 rounded-xl p-5 text-center" data-testid="bank-balance">
                                <p className="text-xs text-violet-300/70 mb-1">{t('staffMenu.balance')}</p>
                                <p className="text-4xl font-bold text-white">${bankData.balance}</p>
                            </div>

                            {/* Inventory */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-slate-300 flex items-center gap-1">
                                    <Gift className="w-3.5 h-3.5 text-emerald-400" /> {t('staffMenu.inventory')}
                                </h3>
                                {bankData.inventory.length > 0 ? (
                                    <div className="space-y-1.5" data-testid="bank-inventory">
                                        {bankData.inventory.map(item => (
                                            <div key={item.value} className="flex items-center justify-between bg-slate-900/60 rounded-lg px-4 py-2.5">
                                                <span className="text-emerald-400 font-semibold">${item.value}</span>
                                                <Badge variant="outline" className="text-xs bg-emerald-500/10 text-emerald-400 border-emerald-500/30">
                                                    {item.available} available
                                                </Badge>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500 text-center py-3">{t('staffMenu.noVouchers')}</p>
                                )}
                            </div>

                            {/* Activity History */}
                            <div className="space-y-2">
                                <h3 className="text-sm font-medium text-slate-300 flex items-center gap-1">
                                    <History className="w-3.5 h-3.5 text-amber-400" /> {t('staffMenu.activity')}
                                </h3>
                                {bankData.activity.length > 0 ? (
                                    <div className="space-y-1" data-testid="bank-activity">
                                        {bankData.activity.slice(0, 15).map((a, i) => (
                                            <div key={i} className="flex items-center justify-between bg-slate-900/40 rounded-lg px-3 py-2 text-sm">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-slate-500 w-16">{formatDate(a.date)}</span>
                                                    <span className="text-slate-300">{a.action}</span>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <span className={a.action === 'Issued' ? 'text-red-400' : 'text-emerald-400'}>
                                                        {a.action === 'Issued' ? '-' : '+'}${a.amount}
                                                    </span>
                                                    <span className="text-xs text-slate-600">{a.type}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-xs text-slate-500 text-center py-3">{t('staffMenu.noActivity')}</p>
                                )}
                            </div>
                        </>
                    ) : (
                        <p className="text-sm text-slate-500 text-center py-8">{t('staffMenu.unableToLoadBank')}</p>
                    )}
                </div>
            </div>
        );
    }

    // === REWARDS MENU ===
    if (phase === 'rewards') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="rewards-menu-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <button onClick={() => setPhase('menu')} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white w-full">
                        <ArrowLeft className="w-4 h-4" /> {t('staffMenu.back')}
                    </button>
                    <div className="space-y-1">
                        <p className="text-sm text-slate-400">{staffData?.staff_name}</p>
                    </div>
                    <div className="space-y-3">
                        <Button
                            data-testid="staff-request-review"
                            className="w-full h-14 bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-700 hover:to-amber-800 justify-start px-4 gap-3 text-base"
                            onClick={() => navigate(`/review-shield/${cardId}`)}
                        >
                            <Star className="w-5 h-5" />
                            {t('staffMenu.requestReview')}
                        </Button>
                        <Button
                            data-testid="staff-issue-voucher"
                            className="w-full h-14 bg-emerald-600 hover:bg-emerald-700 justify-start px-4 gap-3"
                            onClick={() => { resetIssueForm(); setPhase('issue'); }}
                        >
                            <Gift className="w-5 h-5" />
                            {t('staffMenu.issueVoucher')}
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // === ISSUE VOUCHER ===
    if (phase === 'issue') {
        const effectiveValue = getEffectiveValue();
        return (
            <div className="min-h-screen bg-slate-950 p-4" data-testid="staff-issue-screen">
                <div className="max-w-sm mx-auto space-y-5">
                    <button onClick={() => setPhase('rewards')} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> {t('staffMenu.back')}
                    </button>
                    <h2 className="text-lg font-semibold text-white">{t('staffMenu.issueRecoveryVoucher')}</h2>

                    {/* Amount */}
                    <div className="space-y-2">
                        <label className="text-sm text-slate-400 flex items-center gap-1"><DollarSign className="w-3.5 h-3.5" /> {t('staffMenu.amount')}</label>
                        <div className="grid grid-cols-2 gap-2">
                            {presetAmounts.map(v => (
                                <button key={v} data-testid={`voucher-value-${v}`}
                                    onClick={() => { setSelectedValue(v); setShowCustomValue(false); setCustomValue(''); }}
                                    className={`h-14 rounded-lg font-bold text-lg transition-colors ${
                                        selectedValue === v && !showCustomValue ? 'bg-emerald-600 text-white ring-2 ring-emerald-400' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                    }`}>${v}</button>
                            ))}
                        </div>
                        {!showCustomValue ? (
                            <button onClick={() => { setShowCustomValue(true); setSelectedValue(null); }} className="text-xs text-emerald-400 hover:text-emerald-300" data-testid="custom-amount-toggle">{t('staffMenu.customAmount')}</button>
                        ) : (
                            <div className="flex gap-2 mt-1">
                                <Input data-testid="custom-amount-input" type="number" value={customValue} onChange={(e) => setCustomValue(e.target.value)} placeholder={t('staffMenu.amount')} className="bg-slate-900 border-slate-700 text-white text-sm flex-1" autoFocus min={1} max={500} />
                                <button onClick={() => { setShowCustomValue(false); setCustomValue(''); }} className="text-xs text-slate-500">{t('staffMenu.cancel')}</button>
                            </div>
                        )}
                    </div>

                    {/* Expiry */}
                    <div className="space-y-2">
                        <label className="text-sm text-slate-400 flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {t('staffMenu.expiry')}</label>
                        <div className="flex flex-wrap gap-2">
                            {presetExpiry.map(d => (
                                <button key={d} data-testid={`voucher-expiry-${d}`}
                                    onClick={() => { setSelectedExpiry(d); setShowCustomExpiry(false); }}
                                    className={`h-9 px-4 rounded-lg text-xs font-medium transition-colors ${
                                        selectedExpiry === d && !showCustomExpiry ? 'bg-orange-600 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700'
                                    }`}>{d} {t('staffMenu.days')}</button>
                            ))}
                        </div>
                        {!showCustomExpiry ? (
                            <button onClick={() => setShowCustomExpiry(true)} className="text-xs text-orange-400" data-testid="custom-expiry-toggle">{t('staffMenu.customExpiry')}</button>
                        ) : (
                            <div className="flex gap-2 mt-1 items-center">
                                <Input data-testid="custom-expiry-input" type="number" value={customExpiry} onChange={(e) => setCustomExpiry(e.target.value)} placeholder={t('staffMenu.days')} className="bg-slate-900 border-slate-700 text-white text-sm w-24" autoFocus min={1} max={365} />
                                <span className="text-xs text-slate-500">{t('staffMenu.days')}</span>
                                <button onClick={() => { setShowCustomExpiry(false); setCustomExpiry(''); }} className="text-xs text-slate-500">{t('staffMenu.cancel')}</button>
                            </div>
                        )}
                    </div>

                    {/* Reason */}
                    <div className="space-y-2">
                        <label className="text-sm text-slate-400">{t('staffMenu.reason')}</label>
                        <div className="grid grid-cols-2 gap-2">
                            {REASON_CODES.map(r => {
                                const style = STRIPE_STYLES[r.stripe] || STRIPE_STYLES.green;
                                const isSelected = selectedReason === r.code;
                                return (
                                    <button key={r.code} data-testid={`voucher-reason-${r.code}`}
                                        onClick={() => setSelectedReason(r.code)}
                                        className={`h-10 rounded-lg text-xs font-medium px-2 transition-colors border flex items-center gap-1.5 ${
                                            isSelected
                                                ? `${style.bg} ${style.text} border-current ring-1 ring-current/30`
                                                : 'bg-slate-800 text-slate-300 hover:bg-slate-700 border-slate-700'
                                        }`}>
                                        <span className={`w-2 h-2 rounded-full shrink-0 ${style.dot}`} />
                                        {t(r.labelKey)}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Message */}
                    <div className="space-y-2">
                        <label className="text-sm text-slate-400">{t('staffMenu.message')}</label>
                        <Input data-testid="voucher-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder={t('staffMenu.messagePlaceholder')} className="bg-slate-900 border-slate-700 text-white text-sm" maxLength={100} />
                    </div>

                    {issueError && <p className="text-red-400 text-sm">{issueError}</p>}

                    <Button data-testid="voucher-issue-confirm" onClick={issueVoucher} disabled={!effectiveValue || !selectedReason || issuing} className="w-full h-12 bg-emerald-600 hover:bg-emerald-700">
                        {issuing ? <Loader2 className="w-4 h-4 animate-spin" /> : t('staffMenu.issueButton', { value: effectiveValue || '—' })}
                    </Button>
                </div>
            </div>
        );
    }

    // === SUCCESS ===
    if (phase === 'success' && issuedVoucher) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="staff-issue-success">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="w-14 h-14 rounded-full bg-emerald-600/20 flex items-center justify-center mx-auto">
                        <Check className="w-7 h-7 text-emerald-400" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold text-white">{t('staffMenu.voucherIssued')}</h2>
                        <p className="text-3xl font-bold text-emerald-400">${issuedVoucher.value}</p>
                        <p className="text-lg font-mono text-white tracking-wider">{issuedVoucher.code}</p>
                    </div>
                    <div className="bg-slate-900 rounded-lg p-3 text-left space-y-1 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>{t('staffMenu.reason')}</span>
                            <span className="text-slate-200">{t(REASON_CODES.find(r => r.code === issuedVoucher.reason_code)?.labelKey || '')}</span>
                        </div>
                        <div className="flex justify-between text-slate-400">
                            <span>{t('staffMenu.expiry')}</span>
                            <span className="text-slate-200">{parseUtc(issuedVoucher.expires_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                    </div>
                    <Button data-testid="view-customer-voucher" variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800" onClick={() => navigate(`/voucher/${issuedVoucher.code}`)}>
                        {t('staffMenu.viewCustomerVoucher')}
                    </Button>
                    <Button
                        data-testid="activate-vip-btn"
                        className="w-full h-12 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 gap-2"
                        onClick={() => activateVip(issuedVoucher.code)}
                        disabled={vipActivating}
                    >
                        {vipActivating ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Crown className="w-5 h-5" /> {t('staffMenu.activateVip')}</>}
                    </Button>
                    <Button data-testid="issue-another" variant="ghost" className="w-full text-slate-400 hover:text-white" onClick={() => { resetIssueForm(); setPhase('issue'); }}>
                        {t('staffMenu.issueAnother')}
                    </Button>
                    <Button variant="ghost" className="w-full text-slate-500 hover:text-slate-300" onClick={() => setPhase('menu')}>
                        {t('staffMenu.backToMenu')}
                    </Button>
                </div>
            </div>
        );
    }

    // === VERIFY VOUCHER ===
    if (phase === 'verify') {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="verify-voucher-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    {!verifyResult ? (
                        <>
                            <div className="space-y-2">
                                <Search className="w-10 h-10 text-amber-500 mx-auto" />
                                <h1 className="text-lg font-semibold text-white">{t('staffMenu.verifyVoucher')}</h1>
                                <p className="text-sm text-slate-400">{t('staffMenu.enterVoucherCode')}</p>
                            </div>
                            <div className="space-y-3">
                                <Input
                                    data-testid="verify-code-input"
                                    value={verifyCode}
                                    onChange={(e) => {
                                        let val = e.target.value.toUpperCase().replace(/-/g, '');
                                        if (val.length > 2) val = val.slice(0, 2) + '-' + val.slice(2);
                                        if (val.length > 10) val = val.slice(0, 10);
                                        setVerifyCode(val);
                                    }}
                                    onKeyDown={(e) => e.key === 'Enter' && verifyVoucher()}
                                    placeholder={t('staffMenu.codePlaceholder')}
                                    className="text-center text-lg tracking-widest bg-slate-900 border-slate-700 text-white h-14 font-mono"
                                    autoFocus
                                />
                                <Button
                                    data-testid="verify-submit-btn"
                                    onClick={verifyVoucher}
                                    disabled={!verifyCode.trim() || verifyLoading}
                                    className="w-full bg-amber-600 hover:bg-amber-700 h-12"
                                >
                                    {verifyLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : t('staffMenu.verify')}
                                </Button>
                                <Button variant="ghost" className="w-full text-slate-500 hover:text-slate-300" onClick={() => setPhase('menu')}>
                                    {t('staffMenu.cancel')}
                                </Button>
                            </div>
                        </>
                    ) : verifyResult.valid ? (
                        <>
                            <div className="space-y-2">
                                <div className="w-14 h-14 rounded-full bg-emerald-600/20 flex items-center justify-center mx-auto">
                                    <Check className="w-7 h-7 text-emerald-400" />
                                </div>
                                <h2 className="text-lg font-semibold text-emerald-400">{t('staffMenu.voucherValid')}</h2>
                            </div>
                            <div className="bg-slate-900 rounded-lg p-4 text-left space-y-2 text-sm">
                                <div className="flex justify-between text-slate-400">
                                    <span>{t('staffMenu.voucherNumber')}</span>
                                    <span className="text-white font-mono">{verifyResult.code}</span>
                                </div>
                                {/* A prize voucher is an item, not money: its
                                    wording IS the prize, so the staff screen
                                    shows that line instead of a $0 amount.
                                    Gated on the issuer so no other voucher's
                                    screen changes. */}
                                {isPrizeVoucher(verifyResult) ? (
                                    <div className="flex justify-between gap-3 text-slate-400">
                                        <span>{t('staffMenu.prize')}</span>
                                        <span className="text-emerald-400 font-bold text-right"
                                              data-testid="verify-prize-title">{verifyResult.message}</span>
                                    </div>
                                ) : (
                                    <div className="flex justify-between text-slate-400">
                                        <span>{t('staffMenu.amount')}</span>
                                        <span className="text-emerald-400 font-bold text-lg">${verifyResult.value}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-slate-400">
                                    <span>{t('staffMenu.issuedBy')}</span>
                                    <span className="text-slate-200">{verifyResult.issued_by_name || '—'}</span>
                                </div>
                                <div className="flex justify-between text-slate-400">
                                    <span>{t('staffMenu.validUntil')}</span>
                                    <span className="text-slate-200">{parseUtc(verifyResult.expires_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                                </div>
                                <div className="flex justify-between text-slate-400">
                                    <span>{t('staffMenu.status')}</span>
                                    <Badge className="bg-emerald-600/20 text-emerald-400 border-emerald-500/30">Active</Badge>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <Button
                                    data-testid="execute-redeem-btn"
                                    onClick={executeRedeem}
                                    disabled={redeemLoading}
                                    className="w-full h-14 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-base font-bold"
                                >
                                    {redeemLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : t('staffMenu.executeRedeem')}
                                </Button>
                                <Button
                                    data-testid="verify-back-btn"
                                    variant="outline"
                                    className="w-full h-12 border-slate-700 text-white hover:bg-slate-800"
                                    onClick={() => setPhase('menu')}
                                >
                                    {t('staffMenu.back')}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="space-y-2">
                                <div className="w-14 h-14 rounded-full bg-red-600/20 flex items-center justify-center mx-auto">
                                    <XCircle className="w-7 h-7 text-red-400" />
                                </div>
                                <h2 className="text-lg font-semibold text-red-400">{t('staffMenu.voucherNotValid')}</h2>
                                <p className="text-sm text-slate-400" data-testid="verify-result-reason">
                                    {verifyResult.reason === 'NOT_YET_REDEEMABLE'
                                        ? t('vouchers.notValidYetFrom', 'Not valid yet, valid from {{date}}', {
                                            date: verifyResult.redeemable_from
                                                ? new Date(verifyResult.redeemable_from).toLocaleDateString()
                                                : '',
                                        })
                                        : verifyResult.reason}
                                </p>
                            </div>
                            {verifyResult.code && (
                                <div className="bg-slate-900 rounded-lg p-4 text-left space-y-2 text-sm">
                                    <div className="flex justify-between text-slate-400">
                                        <span>{t('staffMenu.voucherNumber')}</span>
                                        <span className="text-white font-mono">{verifyResult.code}</span>
                                    </div>
                                    {verifyResult.value && (
                                        <div className="flex justify-between text-slate-400">
                                            <span>{t('staffMenu.amount')}</span>
                                            <span className="text-slate-200">${verifyResult.value}</span>
                                        </div>
                                    )}
                                    <div className="flex justify-between text-slate-400">
                                        <span>{t('staffMenu.status')}</span>
                                        <Badge className="bg-red-600/20 text-red-400 border-red-500/30 capitalize">{verifyResult.status || 'Invalid'}</Badge>
                                    </div>
                                </div>
                            )}
                            <Button
                                data-testid="verify-close-btn"
                                variant="outline"
                                className="w-full h-12 border-slate-700 text-white hover:bg-slate-800"
                                onClick={() => setPhase('menu')}
                            >
                                {t('staffMenu.closeWindow')}
                            </Button>
                        </>
                    )}
                </div>
            </div>
        );
    }

    // === REDEEM SUCCESS ===
    if (phase === 'redeem_success' && redeemSuccess) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="redeem-success-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-emerald-600/20 flex items-center justify-center mx-auto">
                        <Check className="w-8 h-8 text-emerald-400" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold text-emerald-400">{t('staffMenu.voucherRedeemed')}</h2>
                        {isPrizeVoucher(verifyResult) ? (
                            <p className="text-2xl font-bold text-white" data-testid="redeem-prize-title">
                                {verifyResult.message}
                            </p>
                        ) : (
                            <p className="text-3xl font-bold text-white">${redeemSuccess.value || verifyResult?.value}</p>
                        )}
                        <p className="text-sm font-mono text-slate-400">{redeemSuccess.code || verifyResult?.code}</p>
                    </div>
                    <Button
                        data-testid="redeem-done-btn"
                        className="w-full h-12 bg-emerald-600 hover:bg-emerald-700"
                        onClick={() => { setRedeemSuccess(null); setVerifyResult(null); setVerifyCode(''); setPhase('menu'); }}
                    >
                        {t('staffMenu.backToMenu')}
                    </Button>
                </div>
            </div>
        );
    }

    // === VIP DELIVERY DISPLAY ===
    if (phase === 'vip_display' && vipDelivered) {
        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="vip-delivery-screen">
                <div className="w-full max-w-xs space-y-6 text-center">
                    <div className="w-16 h-16 rounded-full bg-red-600/20 flex items-center justify-center mx-auto">
                        <Crown className="w-8 h-8 text-red-400" />
                    </div>
                    <div className="space-y-1">
                        <h2 className="text-lg font-semibold text-white">{t('staffMenu.vipVoucher')}</h2>
                        <p className="text-4xl font-bold text-emerald-400">${vipDelivered.value}</p>
                        <p className="text-lg font-mono text-white tracking-wider mt-2">{vipDelivered.code}</p>
                    </div>
                    {vipDelivered.message && (
                        <p className="text-sm text-slate-400 italic">{vipDelivered.message}</p>
                    )}
                    <div className="bg-slate-900 rounded-lg p-3 text-left space-y-1 text-sm">
                        <div className="flex justify-between text-slate-400">
                            <span>{t('staffMenu.validUntil')}</span>
                            <span className="text-slate-200">{parseUtc(vipDelivered.expires_at).toLocaleDateString(i18n.language === 'fr' ? 'fr-CA' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                        </div>
                        {vipDelivered.organization_name && (
                            <div className="flex justify-between text-slate-400">
                                <span>{t('staffMenu.from')}</span>
                                <span className="text-slate-200">{vipDelivered.organization_name}</span>
                            </div>
                        )}
                    </div>
                    <Button data-testid="vip-save-photo" variant="outline" className="w-full border-slate-700 text-white hover:bg-slate-800" onClick={() => navigate(`/voucher/${vipDelivered.code}`)}>
                        {t('staffMenu.saveToPhotos')}
                    </Button>
                    <Button variant="ghost" className="w-full text-slate-500 hover:text-slate-300" onClick={() => { setVipDelivered(null); setPhase('menu'); }}>
                        {t('staffMenu.backToMenu')}
                    </Button>
                </div>
            </div>
        );
    }

    // === MY PERFORMANCE ===
    if (phase === 'performance') {
        const motivationalMessages = [
            t('staffMenu.perfGreatWork', 'Great work — keep it going!'),
            t('staffMenu.perfEveryTap', 'Every tap counts.'),
            t('staffMenu.perfYoureOnFire', "You're on fire!"),
        ];
        const motivMsg = motivationalMessages[Math.floor(Date.now() / 86400000) % motivationalMessages.length];

        const getTapCount = () => {
            if (!perfData) return 0;
            if (perfTab === 'today') return perfData.taps_today;
            if (perfTab === 'week') return perfData.taps_week;
            return perfData.taps_month;
        };

        const getEarnings = () => {
            if (!perfData || !perfData.incentive_enabled) return null;
            return (getTapCount() * perfData.rate_per_tap).toFixed(2);
        };

        return (
            <div className="min-h-screen bg-slate-950 flex items-center justify-center p-4" data-testid="staff-performance-screen">
                <div className="w-full max-w-xs space-y-6">
                    <button onClick={() => setPhase('menu')} className="flex items-center gap-1 text-sm text-slate-400 hover:text-white">
                        <ArrowLeft className="w-4 h-4" /> {t('common.back')}
                    </button>

                    <div className="text-center space-y-2">
                        <div className="w-14 h-14 rounded-full mx-auto flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #C5960C, #E8D164, #D4A017)' }}>
                            <TrendingUp className="w-7 h-7 text-white" />
                        </div>
                        <h2 className="text-xl font-bold text-white">{t('staffMenu.myPerformance', 'My Performance')}</h2>
                        <p className="text-sm gold-gradient-text font-medium">{motivMsg}</p>
                    </div>

                    {/* Period Tabs */}
                    <div className="flex gap-1 bg-slate-900 rounded-lg p-1">
                        {[
                            { key: 'today', label: t('staffMenu.perfToday', 'Daily') },
                            { key: 'week', label: t('staffMenu.perfWeek', 'Weekly') },
                            { key: 'month', label: t('staffMenu.perfMonth', 'Monthly') },
                        ].map(tab => (
                            <button
                                key={tab.key}
                                data-testid={`perf-tab-${tab.key}`}
                                onClick={() => setPerfTab(tab.key)}
                                className={`flex-1 py-2 text-sm font-medium rounded-md transition-colors ${
                                    perfTab === tab.key ? 'bg-slate-800 text-white' : 'text-slate-500 hover:text-slate-300'
                                }`}
                            >
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    {perfLoading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-500" /></div>
                    ) : perfData ? (
                        <div className="space-y-4">
                            {/* Tap Count Card */}
                            <div className="bg-slate-900 rounded-xl p-5 text-center border border-slate-800">
                                <p className="text-xs text-slate-500 uppercase tracking-wider mb-2">{t('staffMenu.perfTaps', 'Taps')}</p>
                                <p className="text-5xl font-bold text-white" data-testid="perf-tap-count">{getTapCount()}</p>
                                <p className="text-xs text-slate-500 mt-2">
                                    {perfTab === 'today' ? t('staffMenu.perfToday', 'Today') : perfTab === 'week' ? t('staffMenu.perfThisWeek', 'This Week') : t('staffMenu.perfThisMonth', 'This Month')}
                                </p>
                            </div>

                            {/* Incentive Balance — only if enabled */}
                            {perfData.incentive_enabled && (
                                <div className="bg-slate-900 rounded-xl p-5 border border-slate-800 space-y-3">
                                    <div className="flex items-center justify-between">
                                        <span className="text-xs text-slate-500 uppercase tracking-wider">{t('staffMenu.perfEarnings', 'Earnings')}</span>
                                        <span className="text-xs text-slate-500">${perfData.rate_per_tap}/{t('staffMenu.perfTap', 'tap')}</span>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-3xl font-bold gold-gradient-text" data-testid="perf-earnings">${getEarnings()}</p>
                                        <p className="text-xs text-slate-500 mt-1">{perfTab === 'today' ? t('staffMenu.perfToday', 'Today') : perfTab === 'week' ? t('staffMenu.perfThisWeek', 'This Week') : t('staffMenu.perfThisMonth', 'This Month')}</p>
                                    </div>
                                    <div className="pt-3 border-t border-slate-800">
                                        <div className="flex items-center justify-between text-sm">
                                            <span className="text-slate-400">{t('staffMenu.perfBalance', 'Balance Owed')}</span>
                                            <span className="text-white font-semibold" data-testid="perf-balance">${(perfData.incentive_balance || 0).toFixed(2)}</span>
                                        </div>
                                        {perfData.last_payout_date && (
                                            <div className="flex items-center justify-between text-xs text-slate-500 mt-1">
                                                <span>{t('staffMenu.perfLastPayout', 'Last Payout')}</span>
                                                <span>${perfData.last_payout_amount?.toFixed(2)} — {parseUtc(perfData.last_payout_date).toLocaleDateString()}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <p className="text-center text-slate-500 text-sm">{t('staffMenu.perfNoData', 'No performance data available')}</p>
                    )}
                </div>
            </div>
        );
    }

    return null;
}
